<template>
  <div>
    <v-row class="mb-2" dense>
      <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" sm="3">
        <v-card outlined class="pa-3 fill-height">
          <div class="text-caption grey--text">{{ kpi.label }}</div>
          <div class="text-subtitle-1 font-weight-bold">{{ kpi.value }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-2" align="center" dense>
      <v-col cols="12" sm="5">
        <text-field
          v-model="search"
          :label="$t('finance_search_refunds_placeholder')"
          kind="search"
          clearable
          hideDetails
        />
      </v-col>
      <v-col cols="8" sm="4">
        <dropdown
          v-model="filterStatus"
          :items="statusOptions"
          itemText="label"
          itemValue="value"
          :label="$t('finance_status_label')"
          clearable
          hideDetails
        />
      </v-col>
      <v-col cols="4" sm="3" class="text-right">
        <primary-button @click="openAddDialog">
          <v-icon left small>mdi-plus</v-icon>
          {{ $t("finance_request_refund") }}
        </primary-button>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="filteredRefunds"
        :loading="loading"
        :loading-text="$t('finance_loading')"
        :no-data-text="$t('finance_no_refunds')"
        mobile-breakpoint="0"
        @click:row="openRefund"
      >
        <template #item.orderRef="{ item }">
          <span class="font-weight-bold">{{ item.orderRef }}</span>
        </template>
        <template #item.amount="{ item }">{{ formatCurrency(item.amount, restaurantCurrency) }}</template>
        <template #item.status="{ item }">
          <v-chip small :color="statusMeta(item.status).color" text-color="white">
            {{ $t(statusMeta(item.status).labelKey) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <refund-detail-dialog
      v-model="detailOpen"
      :refund="selectedRefund"
      :can-approve="isAdmin"
      :approving="approving"
      @approve="approveRefund"
    />

    <refund-form-dialog v-model="dialogOpen" :saving="saving" @save="onSaveRefund" />
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import RefundDetailDialog from "./dialogs/RefundDetailDialog.vue";
import RefundFormDialog from "./dialogs/RefundFormDialog.vue";
import { REFUND_STATUS_META, formatCurrency } from "./constants";

const mapRefundDto = (dto) => ({
  id: dto.id,
  orderRef: dto.order_ref,
  customer: dto.customer,
  reason: dto.reason,
  amount: dto.amount,
  status: dto.status,
  approvedBy: dto.approved_by,
  evidence: dto.evidence,
  comment: dto.comment,
  method: dto.method,
  relatedPayout: dto.related_payout,
});

export default {
  name: "FinanceTabRefunds",

  components: { RefundDetailDialog, RefundFormDialog },

  mixins: [hasSnackbar],

  data() {
    return {
      refunds: [],
      loading: false,
      saving: false,
      approving: false,
      search: "",
      filterStatus: null,
      detailOpen: false,
      selectedRefund: null,
      dialogOpen: false,
    };
  },

  computed: {
    restaurantId() {
      return this.$store.state.restaurant.id;
    },
    isAdmin() {
      return this.$store.state.user?.user?.type === "admin";
    },
    headers() {
      return [
        { text: this.$t("finance_order"), value: "orderRef" },
        { text: this.$t("finance_customer"), value: "customer" },
        { text: this.$t("finance_reason"), value: "reason" },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
        { text: this.$t("finance_status_label"), value: "status" },
      ];
    },
    statusOptions() {
      return Object.keys(REFUND_STATUS_META).map((value) => ({
        value,
        label: this.$t(REFUND_STATUS_META[value].labelKey),
      }));
    },
    approvedRefunds() {
      return this.refunds.filter((r) => r.status === "approved");
    },
    totalRefunded() {
      return this.approvedRefunds.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    },
    averageRefund() {
      return this.approvedRefunds.length > 0 ? this.totalRefunded / this.approvedRefunds.length : 0;
    },
    mostCommonReason() {
      const counts = new Map();
      this.refunds.forEach((r) => counts.set(r.reason, (counts.get(r.reason) || 0) + 1));
      let best = "";
      let bestCount = 0;
      counts.forEach((count, reason) => {
        if (count > bestCount) {
          best = reason;
          bestCount = count;
        }
      });
      return best || "—";
    },
    kpis() {
      return [
        { label: this.$t("finance_total_refunded"), value: formatCurrency(this.totalRefunded, this.restaurantCurrency) },
        { label: this.$t("finance_refund_count"), value: String(this.refunds.length) },
        { label: this.$t("finance_average_refund"), value: formatCurrency(this.averageRefund, this.restaurantCurrency) },
        { label: this.$t("finance_most_common_reason"), value: this.mostCommonReason },
      ];
    },
    filteredRefunds() {
      const query = this.search ? this.search.trim().toLowerCase() : "";
      return this.refunds.filter((refund) => {
        if (this.filterStatus && refund.status !== this.filterStatus) return false;
        if (!query) return true;
        return [refund.orderRef, refund.customer, refund.reason]
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
    },
  },

  created() {
    this.refresh();
  },

  methods: {
    formatCurrency,

    statusMeta(status) {
      return REFUND_STATUS_META[status] || { labelKey: "", color: "grey" };
    },

    async refresh() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getRefunds(this.restaurantId);
        this.refunds = (data?.data || []).map(mapRefundDto);
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_refunds_load_error"));
      } finally {
        this.loading = false;
      }
    },

    openRefund(refund) {
      this.selectedRefund = refund;
      this.detailOpen = true;
    },

    openAddDialog() {
      this.dialogOpen = true;
    },

    async onSaveRefund(payload) {
      this.saving = true;
      try {
        const { data } = await restaurantFinanceService.createRefund(
          this.restaurantId,
          payload
        );
        const created = mapRefundDto(data.data || data);
        this.refunds = [created, ...this.refunds];
        this.showSuccessSnackbar(this.$t("finance_refund_requested"));
        this.dialogOpen = false;
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_refund_request_error"));
      } finally {
        this.saving = false;
      }
    },

    // PATCH .../approve knjiži refundaciju u ledger - dok se ne odobri,
    // zahtjev ostaje "pending" i ne utiče na stanje (vidi ticket #223672 v2).
    async approveRefund(refund) {
      this.approving = true;
      try {
        const { data } = await restaurantFinanceService.approveRefund(
          this.restaurantId,
          refund.id
        );
        const updated = mapRefundDto(data.data || data);
        this.refunds = this.refunds.map((r) => (r.id === updated.id ? updated : r));
        this.selectedRefund = updated;
        this.showSuccessSnackbar(this.$t("finance_refund_approved"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_refund_approve_error"));
      } finally {
        this.approving = false;
      }
    },
  },
};
</script>
